import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, apiPost } from "@/lib/api";
import { useSession, setSession, useLogout } from "@/lib/auth";
import { createAuthApiClient, writeStoredSession, type AuthSession } from "@/features/auth/api";
import { Button } from "@/components/ui/Button";
import { queryClient } from "@/lib/queryClient";

export function InvitationPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const session = useSession();
  const { logout } = useLogout();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const invitation = useQuery({ queryKey: ["invitation", token], enabled: !!token, retry: false,
    queryFn: () => api<{ workspace_id: string; role: string; email: string | null; expired: boolean; consumed: boolean }>(`/v1/auth/invitations/${encodeURIComponent(token)}`, { anonymous: true }) });
  const accept = async () => {
    setBusy(true); setError(null);
    try {
      const result = await apiPost<AuthSession & { ok: boolean }>(`/v1/auth/invitations/${encodeURIComponent(token)}/accept`, {});
      if (!result.access_token || !result.user?.id) throw new Error("参加後のログイン情報を取得できませんでした。再度ログインしてください。");
      queryClient.clear(); writeStoredSession(result); setSession({ token: result.access_token, user: { id: result.user.id, email: result.user.email, displayName: result.user.display_name || result.user.email } });
      await queryClient.invalidateQueries(); navigate("/", { replace: true });
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };
  const wrongAccount = !!session && !!invitation.data?.email && invitation.data.email.toLowerCase() !== session.user.email.toLowerCase();
  const switchAccount = async () => {
    setBusy(true);
    await logout();
    navigate("/login", { state: { from: { pathname: "/auth/invitation", search: `?token=${encodeURIComponent(token)}` } } });
  };
  const client = createAuthApiClient();
  // ログイン画面と同じく、サーバがログイン方法の一覧で使えると返した方法だけボタンを出す。
  // 一覧が取れないときは押しても失敗するボタンを出さない。
  const methods = useQuery({ queryKey: ["auth-methods"], retry: false, queryFn: () => client.signInMethods!() });
  return <main className="min-h-screen grid place-items-center bg-surface-base p-6"><section className="max-w-lg rounded border bg-canvas p-6 space-y-4">
    <h1 className="text-xl font-bold">ワークスペースへの招待</h1>
    {!token && <p role="alert">招待トークンがありません。</p>}
    {invitation.isFetching && <p role="status">招待を確認しています…</p>}
    {(error || invitation.error) && <p role="alert" className="text-danger">{error || invitation.error?.message}</p>}
    {invitation.data && (invitation.data.expired || invitation.data.consumed ? <>
      {/* A used or expired link still opens the workspace for someone who already joined it; accept returns their membership. */}
      <p role="alert">{invitation.data.consumed ? "この招待はすでに使われています。参加済みの方は、そのままワークスペースを開けます。" : "この招待は有効期限（発行から 7 日）を過ぎています。参加済みの方は、そのままワークスペースを開けます。まだ参加していない場合は、管理者に招待の再発行を依頼してください。"}</p>
      {session ? <Button disabled={busy} onClick={() => void accept()}>ワークスペースを開く</Button> : <Link className="underline" to="/login" state={{ from: { pathname: "/auth/invitation", search: `?token=${encodeURIComponent(token)}` } }}>ログインして開く</Link>}
    </> : <>
      <p>{invitation.data.email || "あなた"} を {invitation.data.role} として招待しています。</p>
      {session ? <><p>ログイン中: {session.user.email}</p>{wrongAccount && <p className="text-sm text-danger">招待されたメールアドレスと異なります。アカウントを切り替えてください。</p>}<Button disabled={busy || wrongAccount} onClick={() => void accept()}>招待を受け入れる</Button><Button variant="secondary" disabled={busy} onClick={() => void switchAccount()}>別のアカウントでログイン</Button></> : <>
        <p>招待されたメールアドレスでログインしてください。</p>
        <Link className="underline" to="/login" state={{ from: { pathname: "/auth/invitation", search: `?token=${encodeURIComponent(token)}` } }}>メールでログイン</Link>
        {(methods.data?.google || methods.data?.microsoft) && <div className="flex gap-2">{methods.data.google && <Button onClick={() => window.location.assign(client.googleStartUrl(token))}>Google</Button>}{methods.data.microsoft && <Button variant="secondary" onClick={() => window.location.assign(client.microsoftStartUrl(token))}>Microsoft</Button>}</div>}
      </>}
    </>)}
  </section></main>;
}
